import React from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Search as SearchIcon } from "@mui/icons-material";
import {
  getReportSearchHelperText,
  REPORT_SEARCH_MIN_LENGTH,
} from "./mfReportRowSearch";
import { PROFILE_FONT } from "../../../constants/layout";
import { REPORT_COLORS, REPORT_TYPO } from "./mfReportsTheme";

interface ReportTableSearchProps {
  value: string;
  onChange: (value: string) => void;
  matchCount?: number;
  totalCount?: number;
  disabled?: boolean;
  dpNameOptions?: string[];
  selectedDpName?: string;
  onDpNameChange?: (value: string) => void;
}

const ReportTableSearch: React.FC<ReportTableSearchProps> = ({
  value,
  onChange,
  matchCount = 0,
  totalCount = 0,
  disabled = false,
  dpNameOptions = [],
  selectedDpName = "",
  onDpNameChange,
}) => {
  const helperText = getReportSearchHelperText(value, matchCount, totalCount);
  const showDpNameFilter =
    Boolean(onDpNameChange) && dpNameOptions.length > 0;

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            size="small"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={`Search report (min ${REPORT_SEARCH_MIN_LENGTH} characters)`}
            disabled={disabled}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ fontSize: 20, color: REPORT_COLORS.mutedText }}
                  />
                </InputAdornment>
              ),
              endAdornment: value ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => onChange("")}
                    sx={{
                      color: REPORT_COLORS.mutedText,
                      "&:hover": {
                        color: REPORT_COLORS.button,
                        bgcolor: "rgba(30, 58, 95, 0.06)",
                      },
                    }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: PROFILE_FONT,
                fontSize: 14,
                bgcolor: REPORT_COLORS.white,
                borderRadius: "10px",
                "& fieldset": {
                  borderColor: REPORT_COLORS.border,
                },
                "&:hover fieldset": {
                  borderColor: REPORT_COLORS.button,
                },
                "&.Mui-focused fieldset": {
                  borderColor: REPORT_COLORS.button,
                },
              },
            }}
          />
        </Box>
        {showDpNameFilter ? (
          <FormControl
            size="small"
            disabled={disabled}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              flexShrink: 0,
            }}
          >
            <Select
              displayEmpty
              value={selectedDpName}
              onChange={(event) => onDpNameChange?.(event.target.value)}
              renderValue={(selected) =>
                selected ? String(selected) : "All DP Names"
              }
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 14,
                bgcolor: REPORT_COLORS.white,
                borderRadius: "10px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: REPORT_COLORS.border,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: REPORT_COLORS.button,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: REPORT_COLORS.button,
                },
              }}
            >
              <MenuItem value="">All DP Names</MenuItem>
              {dpNameOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </Box>
      {helperText ? (
        <Typography
          sx={{
            ...REPORT_TYPO.body,
            mt: 0.75,
            fontSize: 12,
            color: helperText.includes("No rows")
              ? REPORT_COLORS.loss
              : REPORT_COLORS.mutedText,
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ReportTableSearch;
